import dynamic from 'next/dynamic';

import { useSetAtom } from 'jotai';

import { useUserGroupRestriction } from '@/utils/hooks/acl/useUserGroupRestriction';
import useSPGroups from '@/utils/hooks/swr/group/useSPGroups';
import useSocialProfiles from '@/utils/hooks/swr/useSocialProfiles';
import useTranslation from '@/utils/hooks/translation/useTranslation';
import useActiveGroup from '@/utils/hooks/user/useActiveGroup';

// Utils
import useToggle from '@/utils/hooks/utils/useToggle';

import Icon from '@/components/base/Icon';
import { PopoverContainer, PopoverWrapper } from '@/components/base/Popover';
// Components
import TabsPopover from '@/components/base/Tabs/TabsPopover';
import Tooltip from '@/components/base/Tooltip';
import { ModalUpsell } from '@/components/composite/Modals/ModalUpsell';
import { Footer } from '@/components/layouts/LayoutPlatform/components/Header/components/ManageProfiles/Popover/Footer';
import { GroupsTab } from '@/components/layouts/LayoutPlatform/components/Header/components/ManageProfiles/Popover/GroupsTab';
import { ProfilesTab } from '@/components/layouts/LayoutPlatform/components/Header/components/ManageProfiles/Popover/ProfilesTab';
import { RecapSocialProfiles } from '@/components/layouts/LayoutPlatform/components/Header/components/ManageProfiles/RecapSocialProfiles';
import { TooltipPlanRestrictionContent } from '@/components/layouts/LayoutPlatform/components/Menu/components/TooltipPlanRestriction';
import { menuOverlayAtom } from '@/components/layouts/LayoutPlatform/components/Menu/menuOverlayAtom';
const SaveSelectedProfilesAsGroupModal = dynamic(() =>
    import('@/components/composite/SpGroups/SaveSelectedProfilesAsGroupModal').then(
        (mod) => mod.SaveSelectedProfilesAsGroupModal
    )
);

// Types
import type { SocialProfileType } from '@/types/socialProfileType';

import type { TabsPopoverProps } from '@/components/base/Tabs/TabsPopover';

export function ManageProfiles() {
    const { t } = useTranslation('layout_platform');
    const { t: tCommons } = useTranslation('commons');
    const { activeGroup } = useActiveGroup();
    const { activatedSocialProfiles } = useSocialProfiles({ revalidateIfStale: false });
    const { activatedSpGroups } = useSPGroups({ revalidateIfStale: false });
    const { hasGroupPermission } = useUserGroupRestriction();
    const { isOpen, toggle, params, setParams } = useToggle<SocialProfileType[]>();
    const setMenuOverlay = useSetAtom(menuOverlayAtom);

    const nbProfiles = activatedSocialProfiles?.length || 0;
    const nbGroups = activatedSpGroups?.length || 0;

    const handleGroupsUpsell = () => {
        setMenuOverlay(
            <ModalUpsell
                heading={tCommons('upsell_modal.group.heading')}
                description={tCommons('upsell_modal.group.description')}
                benefits={[
                    {
                        text: tCommons('upsell_modal.group.benefit1'),
                        icon: 'check'
                    },
                    {
                        text: tCommons('upsell_modal.group.benefit2'),
                        icon: 'check'
                    },
                    {
                        text: tCommons('upsell_modal.group.benefit3'),
                        icon: 'check'
                    },
                    {
                        text: tCommons('upsell_modal.group.benefit4'),
                        icon: 'plus'
                    }
                ]}
                illustrationPath="/images/modals/modalUpsell/group.png"
                dismiss={() => setMenuOverlay('')}
            />
        );
    };

    const groupsTabLabel = (
        <>
            {t('manage_profiles.tabs.groups', { count: nbGroups })}
            {!hasGroupPermission && (
                <Tooltip
                    appendTo={document.body}
                    interactive
                    content={<TooltipPlanRestrictionContent />}>
                    <Icon icon="crown" className="ml-1 text-icoBlue" />
                </Tooltip>
            )}
        </>
    );

    const tabs: TabsPopoverProps['tabs'] = [
        {
            label: t('manage_profiles.tabs.all_profiles', { count: nbProfiles }),
            icon: {
                active: 'single-profile-on',
                inactive: 'single-profile'
            }
        },
        {
            label: groupsTabLabel,
            icon: {
                active: 'group-on',
                inactive: 'group'
            },
            onClick: !hasGroupPermission ? handleGroupsUpsell : undefined
        }
    ];

    const handleSaveSelectedProfiles = (profileIds: SocialProfileType['id'][]) => {
        setParams(profileIds.map((id) => activatedSocialProfiles.find((sp) => sp.id === id)));
        toggle();
    };

    return (
        <>
            <PopoverWrapper
                popperOptions={{
                    modifiers: [
                        {
                            name: 'flip',
                            options: {
                                fallbackPlacements: ['auto']
                            }
                        }
                    ]
                }}>
                {({ open, setReferenceElement, setPopperElement, styles, attributes }) => (
                    <>
                        <RecapSocialProfiles setReferenceElement={setReferenceElement} />
                        <PopoverContainer
                            open={open}
                            setPopperElement={setPopperElement}
                            styles={styles}
                            attributes={attributes}>
                            <TabsPopover defaultIndex={Number(Boolean(activeGroup))} tabs={tabs}>
                                <ProfilesTab
                                    handleSaveSelectedProfiles={handleSaveSelectedProfiles}
                                />
                                <GroupsTab />
                            </TabsPopover>
                            <Footer />
                        </PopoverContainer>
                    </>
                )}
            </PopoverWrapper>
            <SaveSelectedProfilesAsGroupModal
                isOpen={isOpen}
                toggle={toggle}
                selectedProfiles={params}
            />
        </>
    );
}
